import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useSocialExchange } from '../hooks/auth-actions.js';
import { useAuthClient } from '../hooks/auth-provider.js';
import { mergeFormClasses } from './shared.js';
import { resolveSocialCallback, takePendingSocialLogin } from './social-login-state.js';
/**
 * Drop into the route the SPA passes as `<SocialSignInButtons callbackPath>` (defaults to
 * `/auth/callback`). On mount it:
 *
 *   1) Takes the `pendingSocialLogin` blob (`{initCode, next}`) the buttons parked in
 *      tab-scoped storage before redirecting to the IdP. Taking it, not reading it: the blob
 *      is good for one callback.
 *   2) Requires the `?state=` the IdP echoed back to byte-match the parked `initCode` before
 *      anything is exchanged. That comparison is what ties the `?code=` in the URL to a
 *      sign-in this browser started; without it, a code obtained by someone else can be
 *      redeemed into this browser's session. The decision lives in
 *      `resolveSocialCallback` — see `social-login-state.ts` for the attack it closes.
 *   3) POSTs `/auth/social/exchange { initCode, returnToCode }` and persists the resulting
 *      session token via the same path as a normal login.
 *   4) Calls `onSuccess(next)` with the original next-path so the app can navigate.
 *
 * Renders nothing (or a tiny status panel) while the exchange is in flight. Fails closed: a
 * missing blob, a missing `state`, a mismatched `state`, or a URL with no `code` all surface a
 * clear error instead of exchanging anyway or silently retrying.
 */
export function SocialCallback(props) {
    const classes = mergeFormClasses(props.classNames);
    const client = useAuthClient();
    const exchange = useSocialExchange();
    const [state, setState] = React.useState({ status: 'exchanging' });
    // Strict mode + dev double-mount guard. The exchange consumes the (initCode, returnToCode)
    // pair on the server — running twice 404s the second call. A ref keeps the second mount
    // from re-firing.
    const ranRef = React.useRef(false);
    React.useEffect(() => {
        if (ranRef.current)
            return;
        ranRef.current = true;
        const fail = (message) => {
            const error = new Error(message);
            setState({ status: 'error', error });
            props.onFailure?.(error);
        };
        // Taking the parked blob also consumes it, which is what makes a replayed callback URL
        // fail: the second visit finds nothing parked. Every branch below therefore runs against
        // storage that is already clear.
        const resolution = resolveSocialCallback({
            currentUrl: window.location.href,
            pending: takePendingSocialLogin(),
        });
        if (resolution.kind === 'error') {
            fail(resolution.message);
            return;
        }
        // Branch 1 — the IdP came back with `?flow=<id>` (no `?code=`). That means the OIDC
        // result wasn't directly usable: most commonly because the IdP's email matches an
        // existing local identity and the user has to confirm ownership (link credentials).
        // Probe the flow and route to the link UI. The pending blob is already consumed, which
        // is what we want — the link page completes via a separate `<LinkAccountForm>` flow that
        // returns its own session token, and a leftover initCode would only confuse a future
        // SocialCallback mount.
        if (resolution.kind === 'link-probe') {
            const { flowId } = resolution;
            void client
                .inspectLinkFlow({ flowId })
                .then((inspection) => {
                if (inspection.isLinkCredentialsFlow) {
                    setState({ status: 'link_required', flowId });
                    if (props.onLinkRequired !== undefined) {
                        props.onLinkRequired(flowId);
                    }
                    return;
                }
                // The flow is back without a code AND isn't in the link state — most likely it was
                // auto-converted to a registration flow (no local account exists for this IdP email
                // and we haven't yet wired the "no silent signup" guard). Surface a clear error
                // rather than letting the user think the page hung.
                fail('The OIDC sign-in returned an unexpected flow state. Try signing in again, or sign up if you don’t have an account.');
            })
                .catch((reason) => {
                fail(reason instanceof Error ? reason.message : String(reason));
            });
            return;
        }
        // Branch 2 — normal exchange path. Reaching here means the URL carried a `code`, this
        // browser had a pending flow parked, and the `state` the IdP echoed back matched it
        // exactly.
        const { next } = resolution;
        void exchange
            .run({
            initCode: resolution.initCode,
            returnToCode: resolution.returnToCode,
            mode: resolution.mode,
        })
            .then(() => {
            setState({ status: 'done' });
            props.onSuccess(next);
        })
            .catch((reason) => {
            fail(reason instanceof Error ? reason.message : String(reason));
        });
        // exchange.run is stable per useAsyncAction; intentionally only run once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (props.renderState !== undefined) {
        return _jsx(_Fragment, { children: props.renderState(state) });
    }
    if (state.status === 'exchanging') {
        return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh', color: '#666' }, children: "Completing sign-in\u2026" }));
    }
    if (state.status === 'link_required') {
        // Default: stay on this page with a message until the host app navigates via
        // `onLinkRequired`. If onLinkRequired wasn't provided, surface a static link.
        return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 32 }, children: _jsxs("div", { style: { maxWidth: 480, textAlign: 'center' }, children: [_jsx("h1", { style: { marginBottom: 12 }, children: "Almost done" }), _jsx("p", { className: classes.hint, children: "That email already has an account. Confirm it's yours to link this sign-in method." }), _jsx("a", { className: classes.link, href: `/auth/link-account?flow=${state.flowId}`, children: "Continue" })] }) }));
    }
    if (state.status === 'error') {
        // Host apps that wire `onFailure` typically navigate elsewhere (e.g. /login with the
        // error in the URL) — render a compact "Redirecting…" while that happens so the user
        // doesn't see a full "Sign-in failed" page flash. When `onFailure` isn't wired we still
        // render the message inline so the user isn't stranded with no feedback.
        if (props.onFailure !== undefined) {
            return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh', color: '#666' }, children: "Redirecting\u2026" }));
        }
        return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 32 }, children: _jsxs("div", { style: { maxWidth: 480, textAlign: 'center' }, children: [_jsx("h1", { style: { marginBottom: 12 }, children: "Sign-in failed" }), _jsx("p", { className: classes.error, role: "alert", children: state.error.message })] }) }));
    }
    return (_jsx("div", { style: { display: 'grid', placeItems: 'center', minHeight: '100vh' }, children: "Signed in." }));
}
//# sourceMappingURL=SocialCallback.js.map